import React, { useState } from 'react';
import { X, Loader2, Shield, ExternalLink, AlertCircle } from 'lucide-react';
import { walletApi } from '../api';
import { depositBreakdown } from '../utils/fees';

interface PaymentSheetProps {
  isOpen: boolean;
  onClose: () => void;
  amount: number;
}

const PaymentSheet: React.FC<PaymentSheetProps> = ({ isOpen, onClose, amount }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const b = depositBreakdown(amount);

  const handlePayFast = async () => {
    setError(null);
    setIsProcessing(true);

    try {
      const response = await walletApi.createPayFastDeposit(amount);
      const url: string | undefined = response?.payment_url;
      if (!url) {
        setIsProcessing(false);
        setError('No payment link was returned. Please try again.');
        return;
      }
      // Hand off to the payment page (real PayFast, or the in-app sandbox page).
      // Wallet is credited server-side once the payment completes.
      try {
        window.location.assign(url);
      } catch {
        setIsProcessing(false);
        setError('Could not open the payment page. Please try again.');
      }
    } catch (err: any) {
      setIsProcessing(false);
      const detail = err?.response?.data?.detail;
      let msg = 'Could not start the payment. Please try again.';
      if (typeof detail === 'string') msg = detail;
      else if (Array.isArray(detail) && detail[0]?.msg) msg = detail[0].msg;
      else if (err?.message) msg = err.message;
      setError(msg);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Sheet */}
      <div className="relative w-full max-w-[375px] max-h-[90dvh] overflow-y-auto bg-white rounded-t-3xl animate-slide-up pb-[env(safe-area-inset-bottom)]">
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-10 h-1 bg-gray-300 rounded-full" />
        </div>

        {/* Header */}
        <div className="px-6 pb-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-gray-800">Add Funds</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-400/70"
              aria-label="Close payment sheet"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2">Adding to wallet</p>
          <p className="text-2xl font-bold text-emerald-600">R{b.credit.toFixed(2)}</p>
        </div>

        <div className="px-6 py-4 space-y-3">
          {/* Fee breakdown — fees are added on top so the wallet gets a clean amount */}
          <div className="bg-gray-50 rounded-xl p-3 text-sm space-y-1.5">
            <div className="flex justify-between text-gray-600">
              <span>Wallet top-up</span>
              <span className="font-medium text-gray-800">R{b.credit.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-gray-500">
              <span>FairPlay fee (1%)</span>
              <span>R{b.fairplay.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-gray-500">
              <span>Payment processing (est.)</span>
              <span>R{b.gateway.toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-bold text-gray-900 border-t border-gray-200 pt-1.5 mt-1">
              <span>Total to pay</span>
              <span>R{b.total.toFixed(2)}</span>
            </div>
          </div>
          <p className="text-[11px] text-gray-400 text-center">
            You get the full R{b.credit.toFixed(2)} in your wallet. The extra covers FairPlay fees and card processing.
          </p>

          {error && (
            <div className="flex items-start space-x-2 bg-red-50 text-red-700 text-sm rounded-xl px-3 py-2">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* PayFast - the only payment rail. Card, EFT, Apple/Google Pay etc.
              are all offered on PayFast's hosted page, so no card data is ever
              entered into this app (PCI DSS SAQ A). */}
          <button
            onClick={handlePayFast}
            disabled={isProcessing}
            className="w-full bg-gradient-to-r from-emerald-600 to-brand-700 text-white py-4 rounded-xl font-medium flex items-center justify-center space-x-2 hover:from-emerald-500 hover:to-brand-600 transition active:scale-[0.98] disabled:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-400/70"
          >
            {isProcessing ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <>
                <span>Pay R{b.total.toFixed(2)} with PayFast</span>
                <ExternalLink className="h-4 w-4" />
              </>
            )}
          </button>

          <p className="text-xs text-center text-gray-500">
            You'll be redirected to PayFast to pay securely by card, EFT or instant
            EFT. Card, Apple Pay and Google Pay are handled there.
          </p>

          {/* Security Badge */}
          <div className="flex items-center justify-center space-x-2 text-xs text-gray-400 pt-2 pb-4">
            <Shield className="h-4 w-4" />
            <span>Secured by PayFast • 256-bit SSL</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSheet;
